/**
 * Registers the service worker (production builds only) and relays the two
 * browser events the UI cares about:
 *
 *   'sw:update'   a new version is downloaded and waiting (UpdatePrompt)
 *   'pwa:install' the browser offers to install the app (account menu)
 */

let updating = false;
let installEvent = null;

// Chrome fires this early, often before React has rendered, so keep it here.
window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  installEvent = event;
  window.dispatchEvent(new Event('pwa:install'));
});
window.addEventListener('appinstalled', () => {
  installEvent = null;
  window.dispatchEvent(new Event('pwa:install'));
});

export const canInstall = () => Boolean(installEvent);

/** Show the browser's install dialog; resolves to true if the user installed. */
export const promptInstall = async () => {
  if (!installEvent) return false;
  const event = installEvent;
  installEvent = null;
  event.prompt();
  const { outcome } = await event.userChoice;
  window.dispatchEvent(new Event('pwa:install'));
  return outcome === 'accepted';
};

/** Activate the waiting version; the page reloads once it takes over. */
export const applyUpdate = (worker) => {
  updating = true;
  worker.postMessage({ type: 'SKIP_WAITING' });
};

const announceUpdate = (worker) => window.dispatchEvent(new CustomEvent('sw:update', { detail: worker }));

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') {
    // A production build tried locally leaves its service worker on
    // localhost:3000, where it would keep serving that build instead of the
    // dev server. Development never uses one, so remove any we find.
    navigator.serviceWorker.getRegistrations().then((all) => all.forEach((r) => r.unregister())).catch(() => {});
    return;
  }

  // Reload only for an update the user asked for, not when the very first
  // service worker takes control of the page.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (updating) window.location.reload();
  });

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`);
      if (registration.waiting && navigator.serviceWorker.controller) announceUpdate(registration.waiting);
      registration.addEventListener('updatefound', () => {
        const worker = registration.installing;
        worker?.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) announceUpdate(worker);
        });
      });
      // Field devices can stay open for days; look for a new version hourly
      // and whenever the app comes back to the foreground.
      setInterval(() => registration.update().catch(() => {}), 60 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') registration.update().catch(() => {});
      });
    } catch (error) {
      console.error('Service worker registration failed', error);
    }
  });
};
